// Lib
import React, { useCallback } from 'react';
import Immutable from 'immutable';
import { ImMNElement, MNElement, MNElementPosition } from '../../../../../../common/elements/elementModelTypes';
import ToolbarTool from '../../ToolbarTool';
import Icon from '../../../../../components/icons/Icon';
import { useDispatch, useSelector } from 'react-redux';
import { moveMultipleElements } from '../../../../../element/actions/elementMoveActions';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import { isLocationAttachedInline } from '../../../../../../common/elements/utils/elementLocationUtils';
import {
    getElementId,
    getElementLocation,
    getLocationParentId,
} from '../../../../../../common/elements/utils/elementPropertyUtils';
import { ELEMENT_MOVE_OPERATIONS } from '../../../../../../common/elements/elementConstants';
import { getDomElementId } from '../../../../../element/utils/elementUtil';
import { reverseTranslate } from '../../../../../../common/maths/geometry/rect';
import { getMiddle } from '../../../../../../common/maths/geometry/number';
import { isEmpty } from 'lodash';
import { canvasZoomScaleSelector } from '../../../../../canvas/store/canvasZoomSelector';

const DEFAULT_POSITION: MNElementPosition = { y: '2rem', x: '100%', attachMode: undefined };
const MANUAL_VERTICAL_CENTER_ADJUSTMENT = 5;

const getInlineCommentSpan = (elementDomNode: HTMLDivElement, elementId: string): HTMLElement | null =>
    elementDomNode?.querySelector(`.InlineComment[data-comment-thread-id="${elementId}"]`) || null;

/**
 * We can't use the zoom context, as the tool is always in the toolbar context, not the inline
 * comment's context, so it would always return 1.
 * Instead, we have to do a bit of a hack and see if the element is on the canvas or not.
 */
const getZoomScale = (inlineCommentSpan: HTMLElement, canvasZoomScale: number) =>
    inlineCommentSpan.closest('#canvas-viewport') ? canvasZoomScale : 1;

/**
 * Finds the position of an inline comment thread relative to its parent element
 * and uses this to position the detached comment thread.
 */
const findInlineCommentThreadPosition = (
    parentElementId: string,
    commentThreadId: string,
    canvasZoomScale = 1,
): MNElementPosition => {
    const elementDomId = getDomElementId(parentElementId);
    const elementDomNode = document.getElementById(elementDomId) as HTMLDivElement;

    if (!elementDomNode) return DEFAULT_POSITION;

    const inlineCommentSpan = getInlineCommentSpan(elementDomNode, commentThreadId);

    if (!inlineCommentSpan) return DEFAULT_POSITION;

    const zoomScale = getZoomScale(inlineCommentSpan, canvasZoomScale);

    const elementBoundingRect = elementDomNode.getBoundingClientRect();
    const inlineCommentRect = inlineCommentSpan.getBoundingClientRect();

    const inlineCommentRelativeRect = reverseTranslate(elementBoundingRect, inlineCommentRect);
    const y =
        getMiddle(inlineCommentRelativeRect.top, inlineCommentRelativeRect.bottom) + MANUAL_VERTICAL_CENTER_ADJUSTMENT;
    const scaledY = y / zoomScale;

    return {
        x: '100%',
        y: `${scaledY}px`,
        attachMode: undefined,
    };
};

type DetachCommentToolProps = {
    selectedElements: Immutable.List<ImMNElement>;
};

/**
 * Removes an inline comment and changes it to be an attached comment aligned with where
 * the inline comment used to be.
 */
const DetachCommentTool: React.FC<DetachCommentToolProps> = (props) => {
    const { selectedElements } = props;

    const canvasZoomScale = useSelector(canvasZoomScaleSelector);

    const dispatch = useDispatch();
    const dispatchDetachInlineComments = useCallback(() => {
        const selectedElementsArray = asObject(selectedElements) as MNElement[];

        const moves = selectedElementsArray?.filter(isLocationAttachedInline).map((el) => {
            const commentThreadId = getElementId(el);
            const parentId = getLocationParentId(el);

            return {
                id: getElementId(el),
                location: {
                    ...getElementLocation(el),
                    position: findInlineCommentThreadPosition(parentId, commentThreadId, canvasZoomScale),
                },
            };
        });

        if (isEmpty(moves)) return;

        dispatch(
            moveMultipleElements({
                moves,
                moveOperation: ELEMENT_MOVE_OPERATIONS.DETACH_COMMENT,
                initialMeasurements: undefined,
                actionOrigin: undefined,
            }),
        );
    }, [selectedElements, canvasZoomScale]);

    const onClick = useCallback(() => {
        dispatchDetachInlineComments();
    }, [dispatchDetachInlineComments]);

    return (
        <div className="DetachCommentTool">
            <ToolbarTool {...props} name="Detach" onClick={onClick}>
                <Icon name="toolbar-detach-comment" />
            </ToolbarTool>
        </div>
    );
};

export default DetachCommentTool;
